import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import PinInput from "react-pin-input";
import api from "../utils/api";
import { UIUtils } from "../utils/uiUtils";
import { useBottomNavBar } from "../contexts/BottomNavBarContext";
import { usePostLoginProcessor } from "../utils/PostLoginProcessor";
import Loader from "../components/Loader";
import { Button } from "../components/Button";
import { TextButton } from "../components/TextButton";
import { recordGoal } from "../utils/abTestUtils";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { isValidIntent, type Intent } from "../utils/types/intent";

type VerificationLocationState = {
  goal?: string;
  heroHeading?: string;
  [key: string]: any;
};

const PRIMARY_COLOR = "#00C8FF";
const PIN_EMPTY_BG = "#E5E5E5";
const PIN_EMPTY_TEXT = "#6B6B6B";

const EmailVerification: React.FC = () => {
  const [verificationCode, setVerificationCode] = useState("");
  const [remainingAttempts, setRemainingAttempts] = useState(0);
  const [isResendEnabled, setIsResendEnabled] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const [resendCountdown, setResendCountdown] = useState(60);
  const [showAttemptsLeft, setShowAttemptsLeft] = useState(false);
  const [Email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loaderMessage, setLoaderMessage] = useState("Loading ...");
  const { setIsVisible } = useBottomNavBar();
  const { processPostLogin } = usePostLoginProcessor();
  const location = useLocation();
  const locationState = location.state as VerificationLocationState | undefined;
  const pinInputRef = useRef<any>(null);
  const hasSubmittedCodeRef = useRef(false);
  const goalTrackedRef = useRef(false);

  const getPinInputs = useCallback(() => {
    if (typeof document === "undefined") {
      return [] as HTMLInputElement[];
    }
    const container = document.getElementById("pincode-input-container");
    if (!container) {
      return [] as HTMLInputElement[];
    }
    return Array.from(
      container.querySelectorAll("input"),
    ) as HTMLInputElement[];
  }, []);

  const updatePinStyles = useCallback(() => {
    const inputs = getPinInputs();
    if (!inputs.length) {
      return;
    }
    inputs.forEach((element) => {
      const hasValue = Boolean(element.value);
      element.style.backgroundColor = hasValue ? PRIMARY_COLOR : PIN_EMPTY_BG;
      element.style.color = hasValue ? "#000000" : PIN_EMPTY_TEXT;
      element.style.border = "none";
      element.style.outline = "none";
      element.style.boxShadow = "none";
      element.style.fontWeight = hasValue ? "700" : "600";
    });
  }, [getPinInputs]);
  const verificationHeading =
    locationState?.heroHeading || "Helping you afford the things you need";

  useEffect(() => {
    setIsVisible(false);
    return () => setIsVisible(true);
  }, [setIsVisible]);

  // Get intent from URL query parameters
  const params = new URLSearchParams(location.search);
  const intent = params.get("intent");

  // Check for 'c' query parameter and prefill code if present
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const codeParam = queryParams.get("c");

    if (codeParam && /^\d+$/.test(codeParam)) {
      setVerificationCode(codeParam);

      // Small delay to ensure the PinInput is mounted
      setTimeout(() => {
        const inputs = getPinInputs();
        if (inputs.length) {
          const codeDigits = codeParam.split("");
          codeDigits.forEach((digit, index) => {
            if (inputs[index]) {
              inputs[index].value = digit;
            }
          });
        }
        updatePinStyles();
      }, 100);
    }
  }, [getPinInputs, location.search, updatePinStyles]);

  useEffect(() => {
    updatePinStyles();
  }, [verificationCode, updatePinStyles]);

  const navigate = useNavigate();
  const { totpUID } = useParams<{ totpUID: string }>();

  const fetchVerificationStatus = useCallback(async () => {
    if (!totpUID) {
      UIUtils.trackException("Missing totpUID");
      UIUtils.showMessageOnSnackBar("Invalid verification link");
      navigate("/");
      return;
    }

    try {
      const response = await api.fetchVerificationStatus(totpUID);
      setEmail(response.data.email);
      setRemainingAttempts(response.data.attempts_left);
      if (
        response.data.attempts_left <= 0 ||
        response.data.status === "INVALID_ATTEMPT" ||
        response.data.status === "EXPIRED"
      ) {
        UIUtils.showMessageOnSnackBar("Please retry logging in.");
        navigate("/");
      }
    } catch (error) {
      UIUtils.trackException("Fetch verification status", error);
      UIUtils.showMessageOnSnackBar("An error occurred. Please try again.");
      navigate("/");
    } finally {
      setIsLoading(false);
      startResendCountdown();
    }
  }, [totpUID, navigate]);

  useEffect(() => {
    fetchVerificationStatus();
  }, [fetchVerificationStatus]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCountdown > 0 && !isResendEnabled) {
      timer = setInterval(() => {
        setResendCountdown((prevCountdown) => prevCountdown - 1);
      }, 1000);
    } else if (resendCountdown === 0) {
      setIsResendEnabled(true);
    }
    return () => clearInterval(timer);
  }, [resendCountdown, isResendEnabled]);

  const startResendCountdown = () => {
    setResendCountdown(60);
    setIsResendEnabled(false);
  };

  const handleResendVerificationCode = async () => {
    if (!totpUID) return;
    if (resendCount >= 2) {
      UIUtils.showMessageOnSnackBar("Maximum resend attempts reached.");
      return;
    }

    try {
      const response = await api.resendVerificationCode(totpUID);
      if (response.status === 200) {
        setResendCount((prevCount) => prevCount + 1);
        startResendCountdown();
        UIUtils.showMessageOnSnackBar("Verification code resent successfully.");
      } else {
        UIUtils.showMessageOnSnackBar("Failed to resend verification code.");
      }
    } catch (error) {
      UIUtils.trackException("Resend verification code", error);
      UIUtils.showMessageOnSnackBar("An error occurred. Please try again.");
    }
  };

  const handleVerifyCode = useCallback(async () => {
    if (!totpUID || verificationCode.length !== 6) return;
    try {
      setIsLoading(true);
      setLoaderMessage("Verifying the details..");
      const lsrc = UIUtils.getCookieValue("lsrc") || "default";
      const response = await api.verifyCode(totpUID, verificationCode, lsrc);

      if (response.data.status === "VERIFIED") {
        const accessToken = response.data.access_token;

        // Set token in API
        api.setToken(accessToken);

        // Process goal if it exists in location state
        if (locationState?.goal) {
          await api.processUserGoal(locationState.goal);
        }

        // Fetch user data (which will now include the new goal)
        const userData = await api.fetchUserData();

        // Process post login with the fetched user data
        const { redirectTo, state } = processPostLogin(
          userData.data,
          accessToken,
          response.data.requestor,
        );

        // Track secondary goal for A/B test if amj exists in session
        if (!goalTrackedRef.current) {
          const amj = sessionStorage.getItem("ab_amj");
          if (amj) {
            goalTrackedRef.current = true;
            recordGoal({
              experiment_id: "ext-landing-hero-goals",
              entity_id: amj,
              entity_id_key: "amj",
              goal_type: "secondary",
              goal_value: "customer signup completed",
            }).catch((error) => {
              UIUtils.trackException(
                "ab_test_goal_update_failed_email_verification",
                error,
              );
            });
          }
        }

        // Include intent in navigation state if valid
        const finalState = {
          ...location.state,
          ...state,
        };
        navigate(redirectTo, {
          replace: true,
          state: finalState,
        });
      } else if (response.data.status === "INCORRECT_OTP") {
        setRemainingAttempts(response.data.attempts_left);
        setShowAttemptsLeft(true);
        if (response.data.attempts_left <= 0) {
          UIUtils.showMessageOnSnackBar(
            "You have exhausted all attempts. Please try again later.",
          );
          navigate("/");
        }
      } else if (response.data.status === "ATTEMPTS_EXHAUSTED") {
        setRemainingAttempts(0);
        setShowAttemptsLeft(true);
        UIUtils.showMessageOnSnackBar(
          "You have exhausted all attempts. Please try again later.",
        );
        navigate("/");
      }
    } catch (error) {
      UIUtils.trackException("Verify OTP", error);
      UIUtils.showMessageOnSnackBar("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
      setLoaderMessage("");
    }
  }, [
    totpUID,
    verificationCode,
    location,
    processPostLogin,
    navigate,
    locationState?.goal,
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleVerifyCode();
  };

  useEffect(() => {
    if (verificationCode.length === 6 && !hasSubmittedCodeRef.current) {
      hasSubmittedCodeRef.current = true;
      handleVerifyCode();
    } else if (verificationCode.length < 6) {
      hasSubmittedCodeRef.current = false;
    }
  }, [verificationCode, handleVerifyCode]);

  const navigateToWelcome = () => {
    // Include intent in navigation state when going back to welcome page
    const navigationState = {
      ...location.state,
      intent: isValidIntent(intent) ? intent : undefined,
    };
    navigate("/", { state: navigationState });
  };

  if (isLoading) {
    return <Loader message={loaderMessage} />;
  }

  return (
    <div className="min-h-screen md:py-8">
      <div className="mx-auto flex w-full max-w-[700px] flex-col items-center pb-12 text-center sm:px-6">
        <h2 className="text-3xl font-black text-gray-900 sm:text-4xl">
          {verificationHeading}
        </h2>
        <section className="mt-4 md:mt-8 w-full rounded-[32px] border border-black/10 bg-white shadow-lg">
          <div className="px-3 py-6 sm:p-10">
            <p className="text-2xl font-black text-gray-900">
              Verify your email
            </p>
            <p className="mt-2 text-base text-secondary">
              A verification code has been sent to {Email}.
            </p>
            <form onSubmit={handleSubmit} className="mt-4 md:mt-8 space-y-6">
              <div id="pincode-input-container" className="flex justify-center">
                <PinInput
                  length={6}
                  onChange={(value: string) => {
                    setVerificationCode(value);
                    updatePinStyles();
                  }}
                  type="numeric"
                  inputMode="number"
                  inputStyle={{
                    border: "none",
                    borderRadius: "16px",
                    margin: "3px 3px 0 0",
                    width: "54px",
                    height: "60px",
                    backgroundColor: "#E4E4E4",
                    color: "#000000",
                    fontSize: "26px",
                    fontWeight: 600,
                  }}
                  inputFocusStyle={{
                    outline: `2px solid ${PRIMARY_COLOR}`,
                    backgroundColor: PRIMARY_COLOR,
                    color: "#000000",
                  }}
                  autoSelect={true}
                  onComplete={(value: string) => {
                    setVerificationCode(value);
                    updatePinStyles();
                  }}
                  ref={pinInputRef}
                  initialValue={verificationCode}
                />
              </div>
              <Button
                text="Verify"
                onClick={() => {}}
                type="submit"
                disabled={verificationCode.length !== 6}
                className="w-full"
              />
            </form>
            <div className="mt-6 space-y-2 text-sm text-secondary">
              <p className="font-bold text-gray-900">
                Didn't receive the code?
              </p>
              <p>Check your spam or junk folder.</p>
            </div>
            {showAttemptsLeft && (
              <p className="mt-4 text-sm text-red">
                {remainingAttempts > 0
                  ? `You have entered an incorrect code. ${remainingAttempts} attempts left!`
                  : "No attempts remaining. Please try again later."}
              </p>
            )}
            {isResendEnabled && resendCount < 2 ? (
              <Button
                text="Resend Verification Code"
                onClick={handleResendVerificationCode}
                className="mt-4 w-full"
                variant="light"
              />
            ) : resendCount < 2 ? (
              <p className="mt-4 text-sm text-secondary">
                Resend code in {resendCountdown} seconds
              </p>
            ) : (
              <p className="mt-4 text-sm text-secondary">
                Already have an account?{" "}
                <TextButton
                  text="Login with email"
                  onClick={navigateToWelcome}
                  className="inline-block"
                />
              </p>
            )}
            <p className="mt-4 text-sm text-secondary">
              If you entered the wrong email please{" "}
              <TextButton
                text="resubmit the form"
                onClick={navigateToWelcome}
                className="inline-block"
              />
            </p>
          </div>
        </section>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mt-4 md:mt-8 flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-black"
        >
          <span aria-hidden="true">&larr;</span> Go back
        </button>
      </div>
    </div>
  );
};

export default EmailVerification;
